// umlaut-check: skip — Datenschluessel ("bestand_erwartet", "t_kritisch"
// usw.) sind technische Bezeichner aus der Prognose-Ausgabe.

import React, {useMemo} from "react";
import {Area, CartesianGrid, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import HeaderWithHelp from "../SalesAnalysis/HeaderWithHelp.jsx";
import {alsIsoDatum, formatDatum, formatDatumKurz, formatMenge, herkunftText, kwZuMontag, kwZuSonntag, sicherheitLabel, useIstSchmal} from "./format.js";

const FARBE_IST = "#495057";
const FARBE_SICHER = "#868e96";
const FARBE_ERWARTET = "#0d6efd";
const FARBE_BESTELLT = "#198754";
const FARBE_PROTOKOLL = "#fd7e14";

const FARBE_T_IDEAL = "#20c997";
const FARBE_T_KRITISCH = "#fd7e14";
const FARBE_T_SPAET = "#e8590c";
const FARBE_T_LEER = "#dc3545";

const MARKEN_ERKLAERUNG = [
    {schluessel: "t_kritisch", label: "T kritisch", farbe: FARBE_T_KRITISCH, bedeutung: "Der Sicherheitsbestand wird angebrochen."},
    {schluessel: "t_leer", label: "T leer", farbe: FARBE_T_LEER, bedeutung: "Der Artikel ist aus."},
    {schluessel: "t_ideal", label: "T ideal", farbe: FARBE_T_IDEAL, bedeutung: "Hier sollte bestellt werden."},
    {schluessel: "t_spaet", label: "T spät", farbe: FARBE_T_SPAET, bedeutung: "Hier ist die letzte Gelegenheit."},
];

const LEGENDE = [
    {schluessel: "ist", label: "Bestand (Ist)", farbe: FARBE_IST, hilfe: "Tatsächlicher Lagerbestand aus der Bestandshistorie, bis heute."},
    {schluessel: "sicher", label: "Sicher", farbe: FARBE_SICHER, hilfe: "Nur von Cosmos bestätigte Mengen zählen als Zulauf. Beantwortet: Womit kann ich rechnen, wenn nichts mehr kommt?"},
    {schluessel: "erwartet", label: "Erwartet", farbe: FARBE_ERWARTET, hilfe: "Bestätigtes plus offen Bestelltes, um die gemessene mittlere Verspätung verschoben. Beantwortet: Was passiert voraussichtlich?"},
    {schluessel: "bestellt", label: "Bestellt", farbe: FARBE_BESTELLT, hilfe: "Alles zum Wunschtermin, so als würde Cosmos exakt wie gewünscht liefern. Beantwortet: Wie sähe es aus, wenn Cosmos liefert wie zugesagt?"},
    {schluessel: "protokoll", label: "Frühere Prognose", farbe: FARBE_PROTOKOLL, hilfe: "Was die Prognose vor einigen Wochen für diese Woche vorhergesagt hatte (Szenario Erwartet) – zeigt, ob sie zuletzt gestimmt hat."},
];

function addTage(iso, tage) {
    const d = new Date(iso + "T00:00:00");
    d.setDate(d.getDate() + tage);
    return alsIsoDatum(d);
}

function tageZwischen(isoA, isoB) {
    const a = new Date(isoA + "T00:00:00");
    const b = new Date(isoB + "T00:00:00");
    return Math.abs((a.getTime() - b.getTime()) / 86400000);
}

/**
 * Fasst Marken zusammen, die am selben oder einem nahen Tag liegen, zu EINER
 * Beschriftung ("T ideal = T spät") - sonst überlagern sich die Recharts-
 * Labels bei eng beieinanderliegenden Terminen unlesbar (Sichtprüfung 1,
 * Befund 11). Jede Marke bekommt trotzdem ihre eigene farbige senkrechte
 * Linie an ihrem echten Datum; nur die Beschriftung sitzt gebündelt an der
 * letzten Linie der Gruppe.
 */
function gruppiereMarken(marken, toleranzTage = 3) {
    const vorhanden = marken.filter((m) => m.wert);
    const sortiert = [...vorhanden].sort((a, b) => (a.wert < b.wert ? -1 : 1));
    const gruppen = [];
    sortiert.forEach((m) => {
        const letzte = gruppen[gruppen.length - 1];
        const vorgaenger = letzte?.mitglieder[letzte.mitglieder.length - 1];
        if (letzte && vorgaenger && tageZwischen(vorgaenger.wert, m.wert) <= toleranzTage) {
            letzte.mitglieder.push(m);
        } else {
            gruppen.push({mitglieder: [m]});
        }
    });
    return gruppen;
}

/**
 * Baut EINE durchgehende Tagesreihe von "heute - 8 Wochen" bis zum Ende des
 * Prognosehorizonts (PLAN.md Abschnitt 4.5: "vergangene Wochen bleiben
 * sichtbar, damit erkennbar ist, ob die Prognose zuletzt gestimmt hat").
 */
function baueReihe({prognose, bestandshistorie, zulaeufe, liefergelegenheiten, prognoseProtokoll}) {
    const szenarien = prognose?.szenarien || {};
    const erwartetReihe = szenarien.erwartet?.tagesreihe || {};
    const tage = Object.keys(erwartetReihe).sort();
    const heuteIso = tage[0] || alsIsoDatum(new Date());
    const horizontEnde = tage[tage.length - 1] || heuteIso;
    const start = addTage(heuteIso, -56);

    const zeilenJeDatum = new Map();
    for (let datum = start; datum <= horizontEnde; datum = addTage(datum, 1)) {
        zeilenJeDatum.set(datum, {datum});
    }

    (bestandshistorie || []).forEach((zeile) => {
        const zielzeile = zeilenJeDatum.get(zeile.stichtag);
        if (zielzeile) {
            zielzeile.bestand_ist = zeile.quantity;
        }
    });

    ["sicher", "erwartet", "bestellt"].forEach((szenario) => {
        const reihe = szenarien[szenario]?.tagesreihe || {};
        Object.entries(reihe).forEach(([datum, wert]) => {
            const zielzeile = zeilenJeDatum.get(datum);
            if (zielzeile) {
                zielzeile[`bestand_${szenario}`] = wert;
            }
        });
    });

    // Anschluss der "Ist"-Fläche an die Prognoselinien: heute traegt beide
    // Werte, damit optisch kein Sprung entsteht.
    const heuteZeile = zeilenJeDatum.get(heuteIso);
    if (heuteZeile && heuteZeile.bestand_ist === undefined && prognose?.bestand_start?.wert !== undefined) {
        heuteZeile.bestand_ist = prognose.bestand_start.wert;
    }

    const zulaufJeTag = new Map();
    (zulaeufe || []).forEach((position) => {
        const kw = position.kw_zusage || position.kw_wunsch;
        const montag = kwZuMontag(kw);
        if (!montag) {
            return;
        }
        const datum = alsIsoDatum(montag);
        const menge = position.kw_zusage ? position.menge_zusage : position.menge_wunsch;
        if (!menge) {
            return;
        }
        const bisher = zulaufJeTag.get(datum) || 0;
        zulaufJeTag.set(datum, bisher + menge);
    });
    zulaufJeTag.forEach((menge, datum) => {
        // Nur innerhalb des angezeigten Fensters markieren - ein Zulauf weit
        // hinter dem Prognosehorizont würde sonst die Achse unnötig dehnen.
        const zielzeile = zeilenJeDatum.get(datum);
        if (zielzeile) {
            zielzeile.zulauf_menge = menge;
        }
    });

    (liefergelegenheiten || []).forEach((datum) => {
        const zielzeile = zeilenJeDatum.get(datum);
        if (zielzeile) {
            zielzeile.liefergelegenheit = true;
        }
    });

    (prognoseProtokoll || []).forEach((zeile) => {
        if (zeile.bestand_erwartet === null || zeile.bestand_erwartet === undefined) {
            return;
        }
        const sonntag = kwZuSonntag(zeile.kw);
        if (!sonntag) {
            return;
        }
        const datum = alsIsoDatum(sonntag);
        const zielzeile = zeilenJeDatum.get(datum);
        if (zielzeile) {
            zielzeile.bestand_protokoll = zeile.bestand_erwartet;
            zielzeile.bestand_tatsaechlich_protokoll = zeile.bestand_tatsaechlich;
        }
    });

    return {reihe: Array.from(zeilenJeDatum.values()).sort((a, b) => (a.datum < b.datum ? -1 : 1)), heuteIso};
}

function CustomTooltip({active, payload, label}) {
    if (!active || !payload || payload.length === 0) {
        return null;
    }
    const zeile = payload[0]?.payload || {};

    return (
        <div className="bg-white border rounded-1 shadow-sm p-2 small" style={{maxWidth: 260}}>
            <div className="fw-semibold mb-1">{formatDatum(label)}</div>
            {zeile.bestand_ist !== undefined && <div>Ist: {formatMenge(zeile.bestand_ist)}</div>}
            {zeile.bestand_sicher !== undefined && <div style={{color: FARBE_SICHER}}>Sicher: {formatMenge(zeile.bestand_sicher)}</div>}
            {zeile.bestand_erwartet !== undefined && <div style={{color: FARBE_ERWARTET}}>Erwartet: {formatMenge(zeile.bestand_erwartet)}</div>}
            {zeile.bestand_bestellt !== undefined && <div style={{color: FARBE_BESTELLT}}>Bestellt: {formatMenge(zeile.bestand_bestellt)}</div>}
            {zeile.bestand_protokoll !== undefined && (
                <div style={{color: FARBE_PROTOKOLL}}>Frühere Prognose (erwartet): {formatMenge(zeile.bestand_protokoll)}</div>
            )}
            {zeile.zulauf_menge ? <div className="text-primary">Zulauf: +{formatMenge(zeile.zulauf_menge)} Dosen</div> : null}
            {zeile.liefergelegenheit ? <div className="text-muted">Liefergelegenheit (Cosmos könnte liefern)</div> : null}
        </div>
    );
}

/**
 * Der Zeitstrahl je Farbton - das Herzstück des Artikel-Blatts (PLAN.md
 * Abschnitt 4.5): tatsächlicher Bestand als Fläche bis heute, drei
 * Prognose-Linien (sicher/erwartet/bestellt), vier Marken als senkrechte
 * Linien, M/Z als waagerechte Linien, Zuläufe als Marker mit Menge,
 * Liefergelegenheiten als dezente Markierungen, vergangene Vorhersagen aus
 * dem Protokoll gestrichelt.
 */
export default function ArtikelZeitstrahl({prognose, bestandshistorie = [], zulaeufe = [], liefergelegenheiten = [], prognoseProtokoll = []}) {
    const schmal = useIstSchmal(576);
    const {reihe, heuteIso} = useMemo(
        () => baueReihe({prognose, bestandshistorie, zulaeufe, liefergelegenheiten, prognoseProtokoll}),
        [prognose, bestandshistorie, zulaeufe, liefergelegenheiten, prognoseProtokoll]
    );

    const werteMax = useMemo(() => {
        const werte = [];
        reihe.forEach((z) => {
            ["bestand_ist", "bestand_sicher", "bestand_erwartet", "bestand_bestellt", "bestand_protokoll"].forEach((schluessel) => {
                if (typeof z[schluessel] === "number") {
                    werte.push(z[schluessel]);
                }
            });
        });
        if (typeof prognose?.mindestbestand?.wert === "number") {
            werte.push(prognose.mindestbestand.wert);
        }
        if (typeof prognose?.zielbestand?.wert === "number") {
            werte.push(prognose.zielbestand.wert);
        }
        return werte.length > 0 ? Math.max(...werte) : 0;
        // Y-Achse muss M, Z und Zuläufe mit abdecken (Sichtprüfung 1, Befund
        // 11: Z lag bei 327160 über der bis dahin nur aus dem Bestand
        // errechneten Achse und war dadurch unsichtbar). Zuläufe stecken
        // bereits in den Prognose-Linien selbst, nicht separat nötig.
    }, [reihe, prognose]);
    // T10: "ganz, ganz wichtig ist die Nulllinie ... gerne mit der Nulllinie
    // abschließen und die Linie dann schon ganz leicht ins Minus gehen
    // lassen" - die Achse reicht nur knapp unter null (−5 % des Maximums,
    // mindestens 1 Einheit), alles darunter wird nicht weiter dargestellt
    // (allowDataOverflow schneidet die Linie an der Achsengrenze ab).
    const yMax = werteMax > 0 ? Math.ceil(werteMax * 1.08) : 1;
    const yMin = werteMax > 0 ? -Math.max(1, Math.ceil(werteMax * 0.05)) : -1;
    const yDomain = [yMin, yMax];
    // Bruchteil der Chart-Höhe von OBEN bis zur Nulllinie - Grundlage für den
    // Farbverlauf, der den Minus-Bereich jeder Linie/Fläche sichtbar
    // ausblasst (T10: "muss sie auch optisch ausgeblasst werden").
    const nullVersatz = Math.max(0, Math.min(1, yMax / (yMax - yMin)));

    if (prognose?.status !== "gerechnet") {
        return (
            <div className="alert alert-secondary mb-0">
                {prognose?.grund_nicht_gerechnet || "Für diesen Farbton liegt keine Prognose vor."}
            </div>
        );
    }

    const erwartet = prognose.szenarien.erwartet;
    const mindestbestand = prognose.mindestbestand;
    const zielbestand = prognose.zielbestand;

    const alleMarken = [
        {schluessel: "heute", label: "Heute", farbe: "#212529", wert: heuteIso},
        ...MARKEN_ERKLAERUNG.map((m) => ({...m, wert: erwartet[m.schluessel]?.wert || null})),
    ];
    const markenGruppen = gruppiereMarken(alleMarken);

    return (
        <div>
            <ResponsiveContainer width="100%" height={schmal ? 320 : 420}>
                <ComposedChart data={reihe} margin={{top: 24, right: 24, left: 8, bottom: 4}}>
                    <defs>
                        {[
                            ["zeitstrahl-grad-ist", FARBE_IST],
                            ["zeitstrahl-grad-sicher", FARBE_SICHER],
                            ["zeitstrahl-grad-erwartet", FARBE_ERWARTET],
                            ["zeitstrahl-grad-bestellt", FARBE_BESTELLT],
                            ["zeitstrahl-grad-protokoll", FARBE_PROTOKOLL],
                        ].map(([id, farbe]) => (
                            // T10: oberhalb der Nulllinie volle Deckkraft, darunter deutlich
                            // blasser - derselbe Verlauf dient Linie UND Fläche.
                            <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
                                <stop offset={nullVersatz} stopColor={farbe} stopOpacity={1} />
                                <stop offset={nullVersatz} stopColor={farbe} stopOpacity={0.28} />
                            </linearGradient>
                        ))}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e9ecef" />
                    <XAxis dataKey="datum" tickFormatter={formatDatumKurz} minTickGap={32} tick={{fontSize: 11}} />
                    <YAxis tickFormatter={formatMenge} tick={{fontSize: 11}} width={70} domain={yDomain} allowDataOverflow />
                    <Tooltip content={<CustomTooltip />} />

                    {liefergelegenheiten
                        .filter((datum) => datum >= reihe[0]?.datum && datum <= reihe[reihe.length - 1]?.datum)
                        .map((datum) => (
                            <ReferenceLine key={`lg-${datum}`} x={datum} stroke="#adb5bd" strokeOpacity={0.35} />
                        ))}

                    {/* Zulauf-Mengen stehen nur im Tooltip (Hover/Tap auf den Punkt) - eine
                        Chart-Beschriftung je Zulauf kollidierte bei mehreren Zuläufen in
                        Kalendernähe unlesbar mit "Heute"/den T-Marken (Befund 11). */}
                    {reihe
                        .filter((z) => z.zulauf_menge)
                        .map((z) => (
                            <ReferenceLine key={`zu-${z.datum}`} x={z.datum} stroke="#0d6efd" strokeDasharray="2 2" strokeOpacity={0.55} />
                        ))}

                    {/* T10: die Nulllinie ist "ganz, ganz wichtig" - kräftig statt als
                        beiläufige Gitterlinie, klar von den übrigen Referenzlinien abgesetzt. */}
                    <ReferenceLine y={0} stroke="#212529" strokeWidth={2} />

                    {/* Befund Sichtprüfung 3 #S4: Klartext vor dem Kürzel, wie auf
                        Folie 6 der Erklärungsseite ("Mindestbestand"/"Zielbestand"). */}
                    {mindestbestand?.wert !== null && mindestbestand?.wert !== undefined && (
                        <ReferenceLine
                            y={mindestbestand.wert}
                            stroke="#7c4a03"
                            strokeDasharray="4 2"
                            label={{value: `Mindestbestand M (${sicherheitLabel(mindestbestand.sicherheit)})`, position: "insideBottomLeft", fill: "#7c4a03", fontSize: 11}}
                        />
                    )}
                    {zielbestand?.wert !== null && zielbestand?.wert !== undefined && (
                        <ReferenceLine
                            y={zielbestand.wert}
                            stroke="#1b4d24"
                            strokeDasharray="4 2"
                            label={{value: `Zielbestand Z (${sicherheitLabel(zielbestand.sicherheit)})`, position: "insideTopLeft", fill: "#1b4d24", fontSize: 11}}
                        />
                    )}

                    {markenGruppen.map((gruppe) =>
                        gruppe.mitglieder.map((m, index) => {
                            const istLetzte = index === gruppe.mitglieder.length - 1;
                            const beschriftung = gruppe.mitglieder.map((x) => x.label).join(" = ");
                            return (
                                <ReferenceLine
                                    key={m.schluessel}
                                    x={m.wert}
                                    stroke={m.farbe}
                                    strokeWidth={m.schluessel === "heute" ? 1 : 2}
                                    label={!schmal && istLetzte ? {value: beschriftung, position: "top", fill: gruppe.mitglieder.length > 1 ? "#212529" : m.farbe, fontSize: 11} : undefined}
                                />
                            );
                        })
                    )}

                    <Area type="monotone" dataKey="bestand_ist" name="Bestand (Ist)" stroke="url(#zeitstrahl-grad-ist)" fill="url(#zeitstrahl-grad-ist)" fillOpacity={0.12} strokeWidth={2} dot={false} isAnimationActive={false} connectNulls={false} />
                    <Line type="monotone" dataKey="bestand_sicher" name="Sicher" stroke="url(#zeitstrahl-grad-sicher)" strokeWidth={1.5} strokeDasharray="4 2" dot={false} isAnimationActive={false} connectNulls={false} />
                    <Line type="monotone" dataKey="bestand_erwartet" name="Erwartet" stroke="url(#zeitstrahl-grad-erwartet)" strokeWidth={2.5} dot={false} isAnimationActive={false} connectNulls={false} />
                    <Line type="monotone" dataKey="bestand_bestellt" name="Bestellt" stroke="url(#zeitstrahl-grad-bestellt)" strokeWidth={1.5} strokeDasharray="6 3" dot={false} isAnimationActive={false} connectNulls={false} />
                    <Line type="monotone" dataKey="bestand_protokoll" name="Frühere Prognose" stroke="url(#zeitstrahl-grad-protokoll)" strokeWidth={1.5} strokeDasharray="2 3" dot={{r: 2}} isAnimationActive={false} connectNulls={false} />
                </ComposedChart>
            </ResponsiveContainer>

            <div className="d-flex flex-wrap gap-3 mt-2 small">
                {LEGENDE.map((l) => (
                    <HeaderWithHelp key={l.schluessel} help={l.hilfe}>
                        <span className="d-inline-flex align-items-center gap-1">
                            <span style={{display: "inline-block", width: 14, height: 3, backgroundColor: l.farbe}} aria-hidden="true" />
                            {l.label}
                        </span>
                    </HeaderWithHelp>
                ))}
            </div>

            <dl className="row small text-muted mt-3 mb-0">
                {MARKEN_ERKLAERUNG.map((m) => (
                    <React.Fragment key={m.schluessel}>
                        <dt className="col-sm-2" style={{color: m.farbe}}>
                            {m.label}
                        </dt>
                        <dd className="col-sm-10">{m.bedeutung}</dd>
                    </React.Fragment>
                ))}
            </dl>
        </div>
    );
}
